"use client";

import { useSession } from "@saas/auth/hooks/use-session";
import { useRouter } from "@shared/hooks/router";
import {
	PENDING_GENERATOR_STATE_KEY,
	saveBlobPendingState,
} from "@shared/lib/pending-state";
import { Dialog, DialogContent } from "@ui/components/dialog";
import { cn } from "@ui/lib";
import dynamic from "next/dynamic";
import { usePathname, useSearchParams } from "next/navigation";
import {
	Suspense,
	createContext,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import type { PropsWithChildren } from "react";

// 两个表单只在弹窗打开时才渲染，却被每个 (saas) 页面静态带上：react-hook-form、
// zod、better-auth 客户端、Turnstile 和约 850 行表单代码都进了首屏 JS。
// 改成按需加载，并在页面空闲时预热，用户点登录时 chunk 基本已在本地。
const formFallback = (
	<div className="flex min-h-[320px] items-center justify-center">
		<span className="loading loading-dots loading-md text-primary" />
	</div>
);
const LoginForm = dynamic(
	() => import("./LoginForm").then((mod) => mod.LoginForm),
	{ ssr: false, loading: () => formFallback },
);
const SignupForm = dynamic(
	() => import("./SignupForm").then((mod) => mod.SignupForm),
	{ ssr: false, loading: () => formFallback },
);

// 创建一个上下文来存储显示登录对话框的状态和方法
type LoginModalContextType = {
	showLoginModal: (redirectTo?: string) => void;
	showSignupModal: (redirectTo?: string) => void;
	hideLoginModal: () => void;
};

const LoginModalContext = createContext<LoginModalContextType | undefined>(
	undefined,
);

// 创建一个Hook来使用登录对话框上下文
export function useLoginModal() {
	const context = useContext(LoginModalContext);

	// 如果上下文不存在，提供无操作的默认实现而不是抛出错误
	if (!context) {
		// 如果在客户端环境且不是生产环境，在控制台显示警告
		if (
			typeof window !== "undefined" &&
			process.env.NODE_ENV !== "production"
		) {
			console.warn(
				"useLoginModal was called outside of LoginModalProvider. This will provide a no-op implementation.",
			);
		}

		return {
			showLoginModal: () => {},
			showSignupModal: () => {},
			hideLoginModal: () => {},
		};
	}

	return context;
}

// 内部组件包含 useSearchParams 调用。它只跑副作用、不渲染任何 DOM，
// 所以必须放在 children 之外：把 children 包进 Suspense 会让整棵树在
// SSR 输出里出现两份（fallback 一份 + 真实内容一份）。
function LoginModalUrlSync({
	onShowLoginModal,
	onHideLoginModal,
	onRedirectPathChange,
	onSetView,
	isOpen,
	redirectPath,
}: {
	onShowLoginModal: (path?: string) => void;
	onHideLoginModal: () => void;
	onRedirectPathChange: (path: string | undefined) => void;
	isOpen: boolean;
	redirectPath: string | undefined;
	onSetView: (view: "login" | "signup") => void;
}) {
	const { user } = useSession();
	const router = useRouter();
	const searchParams = useSearchParams();
	const pathname = usePathname();
	const previousPathname = useRef(pathname);

	// isOpen 挂在 layout 层的 Provider 上，路由切换不会卸载它。弹窗里的「忘记
	// 密码」是个普通 Link，点完底下的页面换了、弹窗还盖在上面，得在这里跟着路径
	// 变化关掉。用 ref 比对而不是直接关：回调是每次渲染新建的内联箭头函数，进了
	// 依赖数组会让 effect 每次渲染都跑，弹窗刚打开就会被关掉。
	useEffect(() => {
		if (previousPathname.current === pathname) {
			return;
		}
		previousPathname.current = pathname;
		onHideLoginModal();
	}, [pathname, onHideLoginModal]);

	// 处理show-login事件
	useEffect(() => {
		const handleShowLogin = (event: Event) => {
			const customEvent = event as CustomEvent;
			const path = customEvent.detail?.redirectTo;
			const state = customEvent.detail?.state;

			if (path) {
				onRedirectPathChange(path);
				console.log("Login modal with redirect to:", path);
			}

			// 现场由各工具页自己写进 IndexedDB（见 @shared/lib/pending-state），
			// 这里只兼容还在用 detail.state 传的调用方
			if (state) {
				void saveBlobPendingState(PENDING_GENERATOR_STATE_KEY, state);
			}

			if (customEvent.detail?.view === "signup") {
				onSetView("signup");
			} else {
				onSetView("login");
			}

			onShowLoginModal();
		};

		window.addEventListener("show-login", handleShowLogin);

		return () => {
			window.removeEventListener("show-login", handleShowLogin);
		};
	}, [onShowLoginModal, onRedirectPathChange]);

	// 处理 URL 参数自动弹出
	useEffect(() => {
		if (user) return; // 如果已登录，不处理
		if (isOpen) return; // 如果模态框已经打开，不处理

		const showLogin = searchParams.get("login") === "true";
		const showSignup = searchParams.get("signup") === "true";

		if (showLogin || showSignup) {
			// 获取当前路径作为登录后的重定向目标
			const currentPath = window.location.pathname;

			// 设置重定向路径
			onRedirectPathChange(currentPath);

			// 设置视图
			if (showLogin) {
				onSetView("login");
			} else {
				onSetView("signup");
			}

			// 打开模态框
			onShowLoginModal();

			// 清理 URL 参数以防止重复触发
			const newUrl = new URL(window.location.href);
			newUrl.searchParams.delete("login");
			newUrl.searchParams.delete("signup");
			router.replace(newUrl.pathname + newUrl.search, { scroll: false });
		}
	}, [
		user,
		searchParams,
		isOpen,
		onShowLoginModal,
		onSetView,
		onRedirectPathChange,
		router,
	]);

	// 如果用户已登录，则关闭登录对话框
	useEffect(() => {
		if (user && isOpen) {
			onHideLoginModal();

			// 如果有重定向路径，则导航到该路径
			if (redirectPath) {
				console.log("Redirecting after login to:", redirectPath);

				// 使用 setTimeout 确保在 DOM 更新后再进行跳转
				setTimeout(() => {
					// 各工具页自己从 IndexedDB 恢复现场并处理过期，这里只负责跳回去
					const currentPath = window.location.pathname;
					if (currentPath !== redirectPath) {
						router.push(redirectPath);
					}
					onRedirectPathChange(undefined);
				}, 0);
			}
		}
	}, [
		user,
		isOpen,
		redirectPath,
		router,
		onHideLoginModal,
		onRedirectPathChange,
	]);

	return null;
}

export function LoginModalProvider({ children }: PropsWithChildren) {
	const [isOpen, setIsOpen] = useState(false);
	const [view, setView] = useState<"login" | "signup">("login");
	const [redirectPath, setRedirectPath] = useState<string | undefined>(
		undefined,
	);

	const showLoginModal = (path?: string) => {
		// Persist redirect path immediately so social/magic-link flows can reuse it
		if (path && typeof window !== "undefined") {
			try {
				localStorage.setItem("modalRedirectPath", path);
				console.log(
					"Saved modalRedirectPath to localStorage from showLoginModal:",
					path,
				);
			} catch (e) {
				console.error(
					"Error saving modalRedirectPath in showLoginModal:",
					e,
				);
			}
		}

		if (path) {
			setRedirectPath(path);
		}
		setView("login");
		setIsOpen(true);
	};

	const showSignupModal = (path?: string) => {
		// Persist redirect path immediately so social/magic-link flows can reuse it
		if (path && typeof window !== "undefined") {
			try {
				localStorage.setItem("modalRedirectPath", path);
				console.log(
					"Saved modalRedirectPath to localStorage from showSignupModal:",
					path,
				);
			} catch (e) {
				console.error(
					"Error saving modalRedirectPath in showSignupModal:",
					e,
				);
			}
		}

		if (path) {
			setRedirectPath(path);
		}
		setView("signup");
		setIsOpen(true);
	};

	const hideLoginModal = () => {
		setIsOpen(false);
		setRedirectPath(undefined);
	};

	// 空闲时预热表单 chunk，不和 hydration 抢主线程
	useEffect(() => {
		const prewarm = () => {
			void import("./LoginForm");
			void import("./SignupForm");
		};
		if (typeof window.requestIdleCallback === "function") {
			const id = window.requestIdleCallback(prewarm, { timeout: 5000 });
			return () => window.cancelIdleCallback(id);
		}
		const id = setTimeout(prewarm, 3000);
		return () => clearTimeout(id);
	}, []);

	return (
		<LoginModalContext.Provider
			value={{ showLoginModal, showSignupModal, hideLoginModal }}
		>
			<Suspense fallback={null}>
				<LoginModalUrlSync
					onShowLoginModal={() => setIsOpen(true)}
					onHideLoginModal={() => setIsOpen(false)}
					onRedirectPathChange={setRedirectPath}
					onSetView={setView}
					isOpen={isOpen}
					redirectPath={redirectPath}
				/>
			</Suspense>
			{children}

			<Dialog open={isOpen} onOpenChange={setIsOpen}>
				{/* 注册表单加上 Turnstile 后在 iPhone SE 这种矮屏会顶出视口，
				    而 Radix 会锁住页面滚动，标题和关闭按钮都够不着 */}
				<DialogContent
					className={cn(
						"sm:max-w-md w-[95%] max-w-md max-h-[90dvh] overflow-y-auto",
						// 手机上照 AIReel 从底部滑上来铺满整屏（10/25）：内容顶着上沿排，
						// 键盘弹起来不容易盖住输入框；sm 以上还是居中弹窗
						"max-sm:inset-0 max-sm:h-dvh max-sm:max-h-none max-sm:w-full max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:content-start max-sm:rounded-none max-sm:border-0 max-sm:pt-[max(1.5rem,env(safe-area-inset-top))] max-sm:pb-[max(1.5rem,env(safe-area-inset-bottom))] max-sm:duration-300",
						// 去掉默认的放大和横向偏移入场，只留上下滑
						"max-sm:data-[state=open]:zoom-in-100 max-sm:data-[state=closed]:zoom-out-100 max-sm:data-[state=open]:slide-in-from-left-0 max-sm:data-[state=closed]:slide-out-to-left-0 max-sm:data-[state=open]:slide-in-from-bottom max-sm:data-[state=closed]:slide-out-to-bottom",
					)}
					// 铺满整屏后没有遮罩可点，× 是唯一的关闭方式，手机上放大点击区
					closeButtonClassName="max-sm:top-3 max-sm:right-3 max-sm:rounded-full max-sm:p-2.5"
				>
					{view === "login" ? (
						<LoginForm
							key={redirectPath}
							redirectTo={redirectPath}
							inModal
							onShowSignup={() => setView("signup")}
						/>
					) : (
						<SignupForm
							redirectTo={redirectPath}
							onShowLogin={() => setView("login")}
							prefillEmail={undefined}
						/>
					)}
				</DialogContent>
			</Dialog>
		</LoginModalContext.Provider>
	);
}
